import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { BsStarFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import {
	fetchWishlist,
	addItemToWishlist,
	removeItemFromWishlist,
} from "../../redux/thunks/wishlistThunks";
import { addToCart, fetchCart, removeFromCart } from "../../redux/thunks/cartThunks";
import { toast } from "react-toastify";
import Header from "../Header/Header";
import ProductImageGallery from "./Components/ProductImageGallery";
import QuantitySelector from "./Components/QuantitySelector";
import ReviewForm from "./Components/ReviewForm";
import ReviewList from "./Components/ReviewList";
import Breadcrumb from "./Components/Breadcrumb";
import Footer from "../../Components/Footer/Footer";
import {
	FacebookShareButton, TwitterShareButton, WhatsappShareButton, EmailShareButton,
	FacebookIcon, TwitterIcon, WhatsappIcon, EmailIcon,
} from "react-share";
import { FaCheck, FaRegCopy } from "react-icons/fa";
import { globalLogout } from "../../Utils/Logout";
import {
	Heart, Share2, ShoppingCart, Zap, Truck, IndianRupee, ShieldCheck, Store, TriangleAlert,
} from "lucide-react";
import PopularProducts from "./Components/PopularProducts";

/* ------------------------------------------------------------------ */
/* Shared style tokens (primary = Tailwind yellow-800, the brown)       */
/* ------------------------------------------------------------------ */
const CARD = "bg-white border border-gray-200 rounded-lg p-4 sm:p-5";
const CARD_TITLE = "text-base font-semibold text-gray-900 mb-3";

const DESCRIPTION_CLASSES =
	"text-sm leading-6 text-gray-700 break-words [&_p]:mb-3 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-yellow-800 [&_blockquote]:bg-yellow-50 [&_blockquote]:px-4 [&_blockquote]:py-2 [&_blockquote]:my-3 [&_blockquote]:italic [&_a]:text-yellow-800 [&_a]:underline [&_strong]:font-semibold [&_hr]:my-4 [&_hr]:border-gray-200 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-100 [&_th]:p-2 [&_th]:text-left [&_td]:border [&_td]:border-gray-300 [&_td]:p-2 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-gray-900 [&_pre]:p-3 [&_pre]:text-gray-100";

/* ------------------------------------------------------------------ */
/* Login prompt modal                                                  */
/* ------------------------------------------------------------------ */
const LoginPromptModal = ({ isOpen, onClose, onConfirm, message }) => {
	if (!isOpen) return null;
	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
			<div className="bg-white rounded-lg p-5 md:p-6 max-w-sm w-full">
				<div className="flex items-center justify-center pb-2">
					<TriangleAlert className="w-14 h-14 text-yellow-500" />
				</div>
				<h3 className="text-lg text-center font-semibold text-gray-900 mb-2">Login required</h3>
				<p className="text-gray-600 mb-5 text-sm text-center">{message}</p>
				<div className="flex gap-3">
					<button
						onClick={onClose}
						className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
					>
						Cancel
					</button>
					<button
						onClick={onConfirm}
						className="flex-1 px-4 py-2 bg-yellow-800 text-white rounded-md hover:bg-yellow-900 text-sm font-medium"
					>
						Login
					</button>
				</div>
			</div>
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* Share popover                                                       */
/* ------------------------------------------------------------------ */
function useOnClickOutside(ref, handler) {
	useEffect(() => {
		const listener = (event) => {
			if (!ref.current || ref.current.contains(event.target)) return;
			handler(event);
		};
		document.addEventListener("mousedown", listener);
		document.addEventListener("touchstart", listener);
		return () => {
			document.removeEventListener("mousedown", listener);
			document.removeEventListener("touchstart", listener);
		};
	}, [ref, handler]);
}

const SocialShare = ({ product }) => {
	const [open, setOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const shareUrl = window.location.href;
	const title = `Check out ${product.name} on our store! Zakkuu`;
	const wrapRef = useRef();

	useOnClickOutside(wrapRef, () => setOpen(false));

	const handleCopy = () => {
		navigator.clipboard.writeText(shareUrl);
		setCopied(true);
		toast.success("Link copied!");
		setTimeout(() => setCopied(false), 1200);
	};

	return (
		<div className="relative" ref={wrapRef}>
			<button
				type="button"
				aria-label="Share"
				onClick={() => setOpen((o) => !o)}
				className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:border-yellow-800 hover:text-yellow-800 transition-colors"
			>
				<Share2 size={17} />
			</button>
			{open && (
				<div className="absolute z-20 right-0 mt-2 min-w-[210px] bg-white border border-gray-200 rounded-lg shadow-xl p-4">
					<p className="text-gray-900 text-sm font-medium pb-2">Share this product</p>
					<div className="flex gap-2 mb-3">
						<FacebookShareButton url={shareUrl} quote={title}><FacebookIcon size={34} round /></FacebookShareButton>
						<TwitterShareButton url={shareUrl} title={title}><TwitterIcon size={34} round /></TwitterShareButton>
						<WhatsappShareButton url={shareUrl} title={title}><WhatsappIcon size={34} round /></WhatsappShareButton>
						<EmailShareButton url={shareUrl} subject={title} body={shareUrl}><EmailIcon size={34} round /></EmailShareButton>
					</div>
					<button
						onClick={handleCopy}
						type="button"
						className="flex items-center justify-center gap-2 px-3 py-2 w-full rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium"
					>
						{copied ? (
							<><FaCheck className="text-green-500" /> Copied!</>
						) : (
							<><FaRegCopy className="text-gray-600" /> Copy link</>
						)}
					</button>
				</div>
			)}
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* Variant helpers (unchanged logic)                                   */
/* ------------------------------------------------------------------ */
function getOptionOrder(product) {
	const availableVariants = product?.variants?.filter((v) => v.status !== "discontinued") || [];
	if (availableVariants.length === 0) return [];
	return availableVariants[0].attributes.map((attr) => attr.option.name);
}

function getOptionsForStep(product, optionOrder, selectedAttrs, stepIndex) {
	if (!product?.variants) return [];
	let filteredVariants = product.variants.filter((v) => v.status !== "discontinued");
	optionOrder.slice(0, stepIndex).forEach((name) => {
		if (selectedAttrs[name] !== undefined) {
			filteredVariants = filteredVariants.filter((variant) => {
				const attr = variant.attributes.find((a) => a.option.name === name);
				return attr && attr.value === selectedAttrs[name];
			});
		}
	});
	const optionName = optionOrder[stepIndex];
	const valuesSet = new Set();
	filteredVariants.forEach((variant) => {
		const attr = variant.attributes.find((a) => a.option.name === optionName);
		if (attr) {
			if (optionName.toLowerCase() === "color") {
				valuesSet.add(JSON.stringify({ value: attr.value, image: variant.images?.[0] || null }));
			} else {
				valuesSet.add(attr.value);
			}
		}
	});
	return optionName.toLowerCase() === "color"
		? Array.from(valuesSet).map((s) => JSON.parse(s))
		: Array.from(valuesSet);
}

function getMatchingVariant(product, optionOrder, selectedAttrs) {
	if (!product?.variants) return null;
	return product.variants.find((variant) => {
		if (variant.status === "discontinued") return false;
		return optionOrder.every((optionName) => {
			const attr = variant.attributes.find((a) => a.option.name === optionName);
			return attr && selectedAttrs[optionName] === attr.value;
		});
	});
}

function getFirstAvailableAttributeCombo(product, optionOrder) {
	let attrs = {};
	for (let i = 0; i < optionOrder.length; ++i) {
		const values = getOptionsForStep(product, optionOrder, attrs, i);
		if (!values.length) break;
		attrs[optionOrder[i]] = optionOrder[i].toLowerCase() === "color" ? values[0].value : values[0];
	}
	return attrs;
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
const ProductDetails = () => {
	const { slug } = useParams();
	const [product, setProduct] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [quantity, setQuantity] = useState(1);
	const [selectedVariant, setSelectedVariant] = useState(null);
	const [showReviewForm, setShowReviewForm] = useState(false);
	const [reviews, setReviews] = useState([]);
	const [breadcrumbItems, setBreadcrumbItems] = useState([]);
	const [loadingWishlistItem, setLoadingWishlistItem] = useState(false);
	const [addingToCart, setAddingToCart] = useState(false);
	const [removingFromCart, setRemovingFromCart] = useState(false);
	const [selectedAttributes, setSelectedAttributes] = useState({});
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [modalMessage, setModalMessage] = useState("");

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const cartItems = useSelector((state) => state.cart.items);
	const token = Cookies.get("ecom_user_token");
	const { items: wishlist, loading: wishlistLoading } = useSelector((state) => state.wishlist);

	/* ---------- data ---------- */
	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				const productResponse = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/getBySlug/${slug}`,
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				setProduct(productResponse.data.product);

				const reviewsResponse = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/api/rating/productRating/${productResponse.data.product._id}`,
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				setReviews(reviewsResponse.data.ratings || []);

				if (token) {
					await dispatch(fetchCart());
					await dispatch(fetchWishlist());
				}
			} catch (err) {
				setError("Failed to load product details. Please try again.");
				globalLogout(err);
			} finally {
				setLoading(false);
			}
		};
		fetchInitialData();
	}, [slug, token, dispatch]);

	/* ---------- breadcrumb (now carries slug so links work) ---------- */
	useEffect(() => {
		if (product && product.category) {
			const buildBreadcrumb = (category, items = []) => {
				if (category) {
					items.unshift({ name: category.name, id: category._id, slug: category.slug });
					if (category.parent) return buildBreadcrumb(category.parent, items);
				}
				return items;
			};
			setBreadcrumbItems([
				{ name: "Home", path: "/" },
				...buildBreadcrumb(product.category).map((item) => ({
					...item,
					path: `/category/${item.slug}`,
				})),
				{ name: product.name, current: true },
			]);
		}
	}, [product]);

	/* ---------- variants ---------- */
	// memoised so the effects below don't re-run on every render
	const optionOrder = useMemo(() => (product ? getOptionOrder(product) : []), [product]);

	useEffect(() => {
		if (!product || !optionOrder.length) return;
		if (Object.keys(selectedAttributes).length === 0) {
			setSelectedAttributes(getFirstAvailableAttributeCombo(product, optionOrder));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product, optionOrder]);

	useEffect(() => {
		if (!product || !optionOrder.length) return;
		const variant = getMatchingVariant(product, optionOrder, selectedAttributes);
		setSelectedVariant(variant ? variant : null);
	}, [selectedAttributes, product, optionOrder]);

	useEffect(() => {
		if (selectedVariant && cartItems) {
			const item = cartItems.find((i) => i.variantId === selectedVariant._id);
			setQuantity(item ? item.quantity : 1);
		}
	}, [selectedVariant, cartItems]);

	const cartItem = selectedVariant
		? cartItems.find((item) => item.variantId === selectedVariant._id)
		: null;
	const isItemInCart = !!cartItem;
	const quantityUnchanged = isItemInCart && quantity === cartItem.quantity;

	const averageRating = reviews.length
		? reviews.reduce((acc, r) => acc + r.ratings, 0) / reviews.length
		: 0;

	const isWishlisted = wishlist.some(
		(item) => item.productId._id === product?._id && item.variantId === selectedVariant?._id
	);

	/* ---------- handlers ---------- */
	const openReviewForm = () => {
		if (token) setShowReviewForm(true);
		else {
			setModalMessage("Please login to write a review");
			setShowLoginModal(true);
		}
	};

	const handleReviewSubmit = async ({ rating, reviewText, reviewImages }) => {
		try {
			const formData = new FormData();
			formData.append("ratings", rating);
			formData.append("reviews", reviewText);
			formData.append("productId", product._id);
			if (reviewImages && reviewImages.length > 0) {
				Array.from(reviewImages).forEach((file) => formData.append("productImages", file));
			}
			await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/rating/addProductRating`, formData, {
				headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
			});
			const response = await axios.get(
				`${process.env.REACT_APP_BACKEND_URL}/api/rating/productRating/${product._id}`,
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setReviews(response.data.ratings || []);
			toast.success("Thank you for your review!");
		} catch (err) {
			globalLogout(err);
		}
	};

	const handleAttributeSelect = (optionName, value, stepIndex) => {
		let newAttributes = {};
		optionOrder.slice(0, stepIndex).forEach((name) => {
			if (selectedAttributes[name] !== undefined) newAttributes[name] = selectedAttributes[name];
		});
		newAttributes[optionName] = value;
		for (let nextStep = stepIndex + 1; nextStep < optionOrder.length; ++nextStep) {
			const nextOption = optionOrder[nextStep];
			const possibleValues = getOptionsForStep(product, optionOrder, newAttributes, nextStep);
			if (!possibleValues.length) break;
			newAttributes[nextOption] =
				nextOption.toLowerCase() === "color" ? possibleValues[0].value : possibleValues[0];
		}
		setSelectedAttributes(newAttributes);
	};

	const isSelectionAvailable = () => !!getMatchingVariant(product, optionOrder, selectedAttributes);

	const requireLogin = (message) => {
		if (token) return false;
		setModalMessage(message);
		setShowLoginModal(true);
		return true;
	};

	const variantIsPurchasable = () => {
		if (!selectedVariant) {
			toast.error("Please select an available variant");
			return false;
		}
		if (selectedVariant.status === "discontinued") {
			toast.error("This variant is no longer available");
			return false;
		}
		if (selectedVariant.stock <= 0) {
			toast.error("This variant is out of stock");
			return false;
		}
		return true;
	};

	const putSelectionInCart = async () => {
		await dispatch(
			addToCart({ productId: product._id, variantId: selectedVariant._id, quantity })
		);
		await dispatch(fetchCart());
	};

	const handleAddToCart = async () => {
		if (requireLogin("You need to login to add items to your cart. Would you like to login now?")) return;
		if (!variantIsPurchasable()) return;
		try {
			setAddingToCart(true);
			await putSelectionInCart();
			toast.success(`${quantity} ${quantity > 1 ? "items" : "item"} added to cart`);
		} catch (err) {
			toast.error("Failed to add to cart. Please try again.");
		} finally {
			setAddingToCart(false);
		}
	};

	// Meesho-style: item already in cart -> button becomes "Go to Cart"
	const handleCartButton = () => {
		if (isItemInCart && quantityUnchanged) navigate("/cart");
		else handleAddToCart();
	};

	const handleBuyNow = async () => {
		if (requireLogin("You need to login to buy this product. Would you like to login now?")) return;
		if (!variantIsPurchasable()) return;
		try {
			setAddingToCart(true);
			if (!isItemInCart || !quantityUnchanged) await putSelectionInCart();
			navigate("/cart");
		} catch (err) {
			toast.error("Something went wrong. Please try again.");
		} finally {
			setAddingToCart(false);
		}
	};

	const handleRemoveFromCart = async () => {
		if (requireLogin("Please login to manage your cart")) return;
		if (!selectedVariant) return;
		try {
			setRemovingFromCart(true);
			await dispatch(removeFromCart({ productId: product._id, variantId: selectedVariant._id }));
			await dispatch(fetchCart());
			toast.success("Item removed from cart");
		} catch (err) {
			toast.error("Failed to remove from cart. Please try again.");
		} finally {
			setRemovingFromCart(false);
		}
	};

	const toggleWishlist = async () => {
		if (requireLogin("You need to login to add items to your wishlist")) return;
		try {
			setLoadingWishlistItem(true);
			const existingItem = wishlist.find(
				(item) => item.productId._id === product._id && item.variantId === selectedVariant?._id
			);
			if (existingItem) {
				await dispatch(removeItemFromWishlist(product._id, selectedVariant._id));
				toast.success("Removed from wishlist");
			} else {
				if (!selectedVariant) {
					toast.error("Please select a variant first");
					return;
				}
				await dispatch(addItemToWishlist(product._id, selectedVariant._id));
				toast.success("Added to wishlist");
			}
		} catch (err) {
			toast.error("Failed to update wishlist. Please try again.");
			console.error("Wishlist error:", err);
		} finally {
			setLoadingWishlistItem(false);
		}
	};

	const handleLoginConfirm = () => {
		setShowLoginModal(false);
		navigate("/login", { state: { from: window.location.pathname } });
	};

	/* ---------- early returns ---------- */
	if (loading)
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-800" />
			</div>
		);

	if (error)
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="text-red-500 text-lg">{error}</div>
			</div>
		);

	if (!product)
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="text-gray-500 text-lg">Product not found</div>
			</div>
		);

	if (!product.published || !product.adminPublished) {
		return (
			<>
				<Header />
				<div className="flex justify-center items-center h-[calc(100vh-80px)]">
					<div className="text-gray-500 text-lg">This product is not currently available</div>
				</div>
			</>
		);
	}

	if (product?.variants?.every((v) => v.status === "discontinued")) {
		return (
			<>
				<Header />
				<div className="flex justify-center items-center h-[calc(100vh-80px)]">
					<div className="text-gray-500 text-lg">This product is no longer available</div>
				</div>
			</>
		);
	}

	/* ---------- derived UI state ---------- */
	const variantReady = selectedVariant && isSelectionAvailable();
	const unavailable =
		!selectedVariant || selectedVariant.stock <= 0 || selectedVariant.status === "discontinued";

	let cartButtonText = "Add to Cart";
	if (addingToCart) cartButtonText = isItemInCart ? "Updating..." : "Adding...";
	else if (selectedVariant?.status === "discontinued") cartButtonText = "Discontinued";
	else if (selectedVariant?.stock <= 0) cartButtonText = "Out of Stock";
	else if (isItemInCart) cartButtonText = quantityUnchanged ? "Go to Cart" : "Update Cart";

	const images = selectedVariant?.images || product.variants?.[0]?.images || [];

	/* ---------- render ---------- */
	return (
		<>
			<Header />
			<main className="mx-auto w-full max-w-[1320px] px-3 sm:px-4 py-4 md:py-6">
				<Breadcrumb items={breadcrumbItems} />

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start">
					{/* ================= LEFT: gallery + CTA ================= */}
					<div className="lg:sticky lg:top-4 space-y-3">
						<div className={CARD}>
							<ProductImageGallery images={images} />
						</div>

						<div className="grid grid-cols-2 gap-3">
							<button
								type="button"
								onClick={handleCartButton}
								disabled={addingToCart || unavailable}
								className={`flex items-center justify-center gap-2 py-3 rounded-md text-sm font-semibold border transition-colors ${addingToCart || unavailable
										? "bg-gray-200 border-gray-200 text-gray-500 cursor-not-allowed"
										: "bg-white border-yellow-800 text-yellow-800 hover:bg-yellow-50"
									}`}
							>
								<ShoppingCart size={18} />
								{cartButtonText}
							</button>
							<button
								type="button"
								onClick={handleBuyNow}
								disabled={addingToCart || unavailable}
								className={`flex items-center justify-center gap-2 py-3 rounded-md text-sm font-semibold transition-colors ${addingToCart || unavailable
										? "bg-gray-200 text-gray-500 cursor-not-allowed"
										: "bg-yellow-800 text-white hover:bg-yellow-900"
									}`}
							>
								<Zap size={18} />
								Buy Now
							</button>
						</div>
					</div>

					{/* ================= RIGHT: stacked cards ================= */}
					<div className="space-y-4 min-w-0">
						{/* Title / price / rating */}
						<div className={CARD}>
							<div className="flex items-start justify-between gap-3">
								<h1 className="text-base sm:text-lg font-medium text-gray-800 leading-snug">
									{product.name}
								</h1>
								<div className="flex items-center gap-2 shrink-0">
									<button
										type="button"
										aria-label="Wishlist"
										onClick={toggleWishlist}
										disabled={wishlistLoading || loadingWishlistItem || !selectedVariant}
										className={`w-9 h-9 flex items-center justify-center rounded-full border transition-colors ${isWishlisted
												? "border-red-300 bg-red-50 text-red-500"
												: "border-gray-300 text-gray-500 hover:border-yellow-800 hover:text-yellow-800"
											}`}
									>
										<Heart size={17} fill={isWishlisted ? "currentColor" : "none"} />
									</button>
									<SocialShare product={product} />
								</div>
							</div>

							{variantReady && (
								<div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 mt-3">
									<span className="text-2xl sm:text-3xl font-bold text-gray-900">
										₹{selectedVariant.price.toFixed(0)}
									</span>
									{selectedVariant.mrp > selectedVariant.price && (
										<span className="text-sm text-gray-400 line-through">
											₹{selectedVariant.mrp.toFixed(0)}
										</span>
									)}
									{selectedVariant.discount > 0 && (
										<span className="text-sm font-semibold text-green-600">
											{selectedVariant.discount}% off
										</span>
									)}
								</div>
							)}

							<div className="flex items-center flex-wrap gap-2 mt-3">
								{reviews.length > 0 && (
									<span className="inline-flex items-center gap-1 rounded-full bg-green-600 text-white text-xs font-semibold px-2.5 py-1">
										{averageRating.toFixed(1)} <BsStarFill className="text-[10px]" />
									</span>
								)}
								<button
									type="button"
									onClick={() =>
										document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" })
									}
									className="text-xs text-gray-500 hover:text-yellow-800"
								>
									{reviews.length > 0
										? `${reviews.length} ${reviews.length === 1 ? "Review" : "Reviews"}`
										: "No reviews yet"}
								</button>
							</div>
						</div>

						{/* Options + availability + quantity */}
						{optionOrder.length > 0 && (
							<div className={CARD}>
								{optionOrder.map((optionName, stepIndex) => {
									const showOption =
										stepIndex === 0 ||
										optionOrder.slice(0, stepIndex).every((opt) => selectedAttributes[opt]);
									if (!showOption) return null;
									const possibleValues = getOptionsForStep(
										product, optionOrder, selectedAttributes, stepIndex
									);
									if (possibleValues.length === 0) return null;
									const isColor = optionName.toLowerCase() === "color";

									return (
										<div key={optionName} className="mb-4 last:mb-0">
											<h3 className={CARD_TITLE}>Select {optionName}</h3>
											<div className="flex flex-wrap gap-2">
												{possibleValues.map((valueObj, index) => {
													const value = isColor ? valueObj.value : valueObj;
													const image = isColor ? valueObj.image : null;
													const selected = selectedAttributes[optionName] === value;

													if (image) {
														return (
															<button
																key={index}
																type="button"
																onClick={() => handleAttributeSelect(optionName, value, stepIndex)}
																className="flex flex-col items-center gap-1 w-20"
															>
																<img
																	src={image}
																	alt={value}
																	className={`w-20 h-20 object-cover rounded-lg border-2 ${selected ? "border-yellow-800" : "border-gray-200"
																		}`}
																/>
																<span
																	className={`text-xs ${selected ? "text-yellow-800 font-semibold" : "text-gray-600"
																		}`}
																>
																	{value}
																</span>
															</button>
														);
													}

													return (
														<button
															key={index}
															type="button"
															onClick={() => handleAttributeSelect(optionName, value, stepIndex)}
															className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-colors ${selected
																	? "border-yellow-800 bg-yellow-50 text-yellow-800"
																	: "border-gray-300 text-gray-700 hover:border-yellow-800"
																}`}
														>
															{value}
														</button>
													);
												})}
											</div>
										</div>
									);
								})}

								{Object.keys(selectedAttributes).length === optionOrder.length && !isSelectionAvailable() && (
									<div className="mt-4 p-3 rounded-md bg-yellow-100 text-yellow-800 text-sm">
										This combination is not available
									</div>
								)}

								{variantReady && (
									<div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between gap-4">
										<div>
											<p className="text-xs text-gray-500">Availability</p>
											<p
												className={`text-sm font-semibold ${selectedVariant.status === "discontinued"
														? "text-red-600"
														: selectedVariant.stock > 0
															? "text-green-600"
															: "text-red-500"
													}`}
											>
												{selectedVariant.status === "discontinued"
													? "Discontinued"
													: selectedVariant.stock > 0
														? "In Stock"
														: "Out of Stock"}
											</p>
										</div>

										<div className="flex flex-col items-end gap-1">
											<QuantitySelector
												quantity={quantity}
												setQuantity={setQuantity}
												maxStock={selectedVariant.stock}
												disabled={addingToCart || selectedVariant.status === "discontinued"}
											/>
											{isItemInCart && (
												<button
													type="button"
													onClick={handleRemoveFromCart}
													disabled={removingFromCart}
													className="text-xs text-red-600 hover:underline disabled:opacity-60"
												>
													{removingFromCart ? "Removing..." : "Remove from cart"}
												</button>
											)}
										</div>
									</div>
								)}
							</div>
						)}

						{!variantReady && optionOrder.length > 0 && Object.keys(selectedAttributes).length < optionOrder.length && (
							<div className="bg-gray-100 p-4 rounded-md text-sm text-gray-700">
								Please select options to see availability
							</div>
						)}

						{variantReady && (
							<>
								{/* Delivery + seller */}
								<div className={CARD}>
									<h3 className={CARD_TITLE}>Delivery &amp; Seller</h3>
									<div className="space-y-3 text-sm text-gray-700">
										<p className="flex items-center gap-3">
											<Truck size={18} className="text-yellow-800 shrink-0" />
											<span>
												Delivery available only in{" "}
												<strong className="text-yellow-800">Gautam Buddh Nagar</strong>
											</span>
										</p>
										<p className="flex items-center gap-3">
											<IndianRupee size={18} className="text-yellow-800 shrink-0" />
											<span>
												Payment mode:{" "}
												<strong className="text-yellow-800">Cash on Delivery</strong>
											</span>
										</p>
										{product.seller?.businessName && (
											<p className="flex items-center gap-3">
												<Store size={18} className="text-yellow-800 shrink-0" />
												<span>
													Sold by <strong className="text-gray-900">Zakku</strong>
												</span>
											</p>
										)}
									</div>
								</div>

								{/* Product highlights */}
								{product.specifications && product.specifications.length > 0 && (
									<div className={CARD}>
										<h3 className={CARD_TITLE}>Product Highlights</h3>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
											{product.specifications.map((spec, idx) => (
												<div key={idx} className="min-w-0">
													<p className="text-xs text-gray-500">{spec.label}</p>
													<p className="text-sm text-gray-800 break-words">{spec.value}</p>
												</div>
											))}
										</div>
									</div>
								)}

								{/* Description */}
								{product.description && (
									<div className={CARD}>
										<h3 className={CARD_TITLE}>Product Details</h3>
										{/* TIP: sanitise with DOMPurify before rendering seller-entered HTML */}
										<div
											className={DESCRIPTION_CLASSES}
											dangerouslySetInnerHTML={{ __html: product.description }}
										/>
									</div>
								)}
							</>
						)}

						{/* Reviews */}
						<ReviewList reviews={reviews} onOpenReviewForm={openReviewForm} />

						{/* Trust strip */}
						<div className="grid grid-cols-3 gap-2 rounded-lg bg-yellow-50 border border-yellow-100 p-4 text-center">
							{[
								{ icon: IndianRupee, label: "Cash on Delivery" },
								{ icon: Truck, label: "Local Delivery" },
								{ icon: ShieldCheck, label: "Secure Ordering" },
							].map(({ icon: Icon, label }) => (
								<div key={label} className="flex flex-col items-center gap-2">
									<span className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-yellow-800 shadow-sm">
										<Icon size={20} />
									</span>
									<span className="text-xs text-gray-700">{label}</span>
								</div>
							))}
						</div>
					</div>
				</div>

				<div className="mt-10">
					<PopularProducts />
				</div>
			</main>

			{showReviewForm && (
				<ReviewForm
					productId={product._id}
					onClose={() => setShowReviewForm(false)}
					onReviewSubmit={handleReviewSubmit}
				/>
			)}

			<Footer />
			<LoginPromptModal
				isOpen={showLoginModal}
				onClose={() => setShowLoginModal(false)}
				onConfirm={handleLoginConfirm}
				message={modalMessage}
			/>
		</>
	);
};

export default ProductDetails;